import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import {
  Lock, RefreshCw, Mail, Phone as PhoneIcon, MessageSquare,
  Inbox, PartyPopper, Megaphone, Save, Trash2, Calendar, Users,
} from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminPage() {
  const [token, setToken] = useState("");
  const [authed, setAuthed] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [catering, setCatering] = useState([]);
  const [announcement, setAnnouncement] = useState({ message_en: "", message_es: "", active: true });
  const [loading, setLoading] = useState(false);

  const headers = useCallback(() => ({ "X-Admin-Token": token }), [token]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [c, cat, ann] = await Promise.all([
        axios.get(`${API}/contact`, { headers: headers() }),
        axios.get(`${API}/catering`, { headers: headers() }),
        axios.get(`${API}/announcement`),
      ]);
      setContacts(c.data || []);
      setCatering(cat.data || []);
      if (ann.data) setAnnouncement({
        message_en: ann.data.message_en || "",
        message_es: ann.data.message_es || "",
        active: !!ann.data.active,
      });
      setAuthed(true);
    } catch {
      toast.error("Unauthorized", { description: "Invalid admin token." });
      setAuthed(false);
    } finally {
      setLoading(false);
    }
  }, [headers]);

  const onLogin = (e) => {
    e.preventDefault();
    if (token) fetchAll();
  };

  const saveAnnouncement = async () => {
    try {
      await axios.put(`${API}/admin/announcement`, announcement, { headers: headers() });
      toast.success("Announcement saved");
    } catch {
      toast.error("Failed to save");
    }
  };

  const clearAnnouncement = async () => {
    try {
      await axios.delete(`${API}/admin/announcement`, { headers: headers() });
      setAnnouncement({ message_en: "", message_es: "", active: true });
      toast.success("Announcement cleared");
    } catch {
      toast.error("Failed to clear");
    }
  };

  return (
    <main data-testid="admin-page" className="min-h-screen bg-[#F9F6F0] text-[#232321]">
      <div className="border-b border-[#E2D9CD] bg-[#F9F6F0]/85 backdrop-blur-xl sticky top-0 z-30">
        <div className="mx-auto max-w-6xl px-6 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="admin-back-link">
            <span className="h-10 w-10 rounded-xl bg-[#F9F6F0] border border-[#E2D9CD] p-0.5 overflow-hidden shadow-sm flex items-center justify-center">
              <img
                src="/images/logo.jpg"
                alt="La Hacienda Michoacana"
                className="w-full h-full object-cover rounded-lg"
              />
            </span>
            <span className="font-serif-display text-lg">La Hacienda · Admin</span>
          </Link>
          {authed && (
            <button
              data-testid="admin-refresh-btn"
              onClick={fetchAll}
              className="inline-flex items-center gap-2 rounded-full border border-[#E2D9CD] px-4 py-2 text-[13px] font-medium hover:bg-[#EADCCF]"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} strokeWidth={1.5} />
              Refresh
            </button>
          )}
        </div>
      </div>

      <div className="mx-auto max-w-6xl px-6 py-12">
        {!authed ? (
          <div className="max-w-md mx-auto">
            <div className="text-center mb-8">
              <div className="h-12 w-12 rounded-full bg-[#EADCCF] mx-auto flex items-center justify-center">
                <Lock className="h-5 w-5 text-[#C05A3A]" strokeWidth={1.5} />
              </div>
              <h1 className="mt-5 font-serif-display text-3xl">Admin access</h1>
              <p className="text-sm text-[#5C5C59] mt-2">
                Enter the admin token to manage submissions and content.
              </p>
            </div>
            <form onSubmit={onLogin} className="space-y-4" data-testid="admin-login-form">
              <input
                data-testid="admin-token-input"
                type="password"
                value={token}
                onChange={(e) => setToken(e.target.value)}
                placeholder="Admin token"
                className="w-full rounded-xl border border-[#E2D9CD] bg-white/60 px-4 py-3 outline-none focus:border-[#C05A3A]"
              />
              <button
                data-testid="admin-login-btn"
                type="submit"
                disabled={loading}
                className="w-full rounded-full bg-[#C05A3A] hover:bg-[#A54C30] disabled:opacity-60 text-[#F9F6F0] py-3 text-[14px] font-medium"
              >
                {loading ? "Verifying…" : "Sign in"}
              </button>
            </form>
          </div>
        ) : (
          <Tabs defaultValue="contact" className="w-full">
            <TabsList data-testid="admin-tabs" className="bg-[#EADCCF]/60 border border-[#E2D9CD]">
              <TabsTrigger value="contact" data-testid="tab-contact">
                <Inbox className="h-4 w-4 mr-2" strokeWidth={1.5} />
                Contact <span className="ml-2 text-[11px] opacity-60">{contacts.length}</span>
              </TabsTrigger>
              <TabsTrigger value="catering" data-testid="tab-catering">
                <PartyPopper className="h-4 w-4 mr-2" strokeWidth={1.5} />
                Catering <span className="ml-2 text-[11px] opacity-60">{catering.length}</span>
              </TabsTrigger>
              <TabsTrigger value="announcement" data-testid="tab-announcement">
                <Megaphone className="h-4 w-4 mr-2" strokeWidth={1.5} />
                Announcement
              </TabsTrigger>
            </TabsList>

            <TabsContent value="contact" className="mt-8">
              <ListHeader title="Contact submissions" count={contacts.length} testid="admin-count" />
              <SubmissionList items={contacts} testid="contact" />
            </TabsContent>

            <TabsContent value="catering" className="mt-8">
              <ListHeader title="Catering requests" count={catering.length} testid="admin-catering-count" />
              <CateringList items={catering} />
            </TabsContent>

            <TabsContent value="announcement" className="mt-8 max-w-2xl">
              <h2 className="font-serif-display text-3xl">Site announcement</h2>
              <p className="text-sm text-[#5C5C59] mt-2 mb-6">
                Shown as a thin banner at the top of the homepage. Leave fields empty and click clear to remove.
              </p>
              <div className="space-y-4 bg-[#EADCCF]/40 border border-[#E2D9CD] rounded-2xl p-6">
                <div>
                  <label className="text-[11px] uppercase tracking-[0.22em] text-[#5C5C59]">English</label>
                  <textarea
                    data-testid="admin-ann-en"
                    rows={2}
                    value={announcement.message_en}
                    onChange={(e) => setAnnouncement((a) => ({ ...a, message_en: e.target.value }))}
                    className="mt-2 w-full rounded-xl border border-[#E2D9CD] bg-white/60 px-4 py-3 outline-none focus:border-[#C05A3A] resize-none"
                    placeholder="Closed Christmas Day · Open early on Saturday…"
                  />
                </div>
                <div>
                  <label className="text-[11px] uppercase tracking-[0.22em] text-[#5C5C59]">Spanish</label>
                  <textarea
                    data-testid="admin-ann-es"
                    rows={2}
                    value={announcement.message_es}
                    onChange={(e) => setAnnouncement((a) => ({ ...a, message_es: e.target.value }))}
                    className="mt-2 w-full rounded-xl border border-[#E2D9CD] bg-white/60 px-4 py-3 outline-none focus:border-[#C05A3A] resize-none"
                    placeholder="Cerrado el 25 de diciembre · Abrimos temprano el sábado…"
                  />
                </div>
                <label className="flex items-center gap-2 text-sm text-[#5C5C59]">
                  <input
                    type="checkbox"
                    data-testid="admin-ann-active"
                    checked={announcement.active}
                    onChange={(e) => setAnnouncement((a) => ({ ...a, active: e.target.checked }))}
                  />
                  Show on site
                </label>
                <div className="flex gap-3 pt-2">
                  <button
                    data-testid="admin-ann-save"
                    onClick={saveAnnouncement}
                    className="inline-flex items-center gap-2 rounded-full bg-[#C05A3A] hover:bg-[#A54C30] text-[#F9F6F0] px-5 py-2.5 text-[13px] font-medium"
                  >
                    <Save className="h-4 w-4" strokeWidth={1.5} />
                    Save
                  </button>
                  <button
                    data-testid="admin-ann-clear"
                    onClick={clearAnnouncement}
                    className="inline-flex items-center gap-2 rounded-full border border-[#E2D9CD] text-[#5C5C59] hover:text-[#232321] hover:bg-[#EADCCF] px-5 py-2.5 text-[13px] font-medium"
                  >
                    <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                    Clear
                  </button>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        )}
      </div>
    </main>
  );
}

function ListHeader({ title, count, testid }) {
  return (
    <div className="flex items-baseline justify-between mb-5">
      <h2 className="font-serif-display text-3xl md:text-4xl">{title}</h2>
      <span data-testid={testid} className="text-sm text-[#5C5C59]">{count} total</span>
    </div>
  );
}

function SubmissionList({ items, testid }) {
  if (items.length === 0)
    return <Empty>No submissions yet.</Empty>;
  return (
    <ul className="space-y-4">
      {items.map((it) => (
        <li
          key={it.id}
          data-testid={`admin-submission-${it.id}`}
          className="rounded-2xl border border-[#E2D9CD] bg-[#EADCCF]/40 p-6"
        >
          <Header it={it} />
          <Contacts it={it} />
          <div className="flex gap-3 mt-3">
            <MessageSquare className="h-4 w-4 mt-1 text-[#C05A3A]" strokeWidth={1.5} />
            <p className="text-[#232321] leading-relaxed whitespace-pre-wrap">{it.message}</p>
          </div>
        </li>
      ))}
    </ul>
  );
}

function CateringList({ items }) {
  if (items.length === 0) return <Empty>No catering requests yet.</Empty>;
  return (
    <ul className="space-y-4">
      {items.map((it) => (
        <li
          key={it.id}
          data-testid={`admin-catering-${it.id}`}
          className="rounded-2xl border border-[#E2D9CD] bg-[#EADCCF]/40 p-6"
        >
          <Header it={it} />
          <Contacts it={it} />
          <div className="flex flex-wrap gap-x-5 gap-y-2 text-sm text-[#5C5C59] mt-3">
            {it.event_date && (
              <span className="flex items-center gap-2">
                <Calendar className="h-4 w-4" strokeWidth={1.5} /> {it.event_date}
              </span>
            )}
            {it.guest_count != null && (
              <span className="flex items-center gap-2">
                <Users className="h-4 w-4" strokeWidth={1.5} /> {it.guest_count} guests
              </span>
            )}
            {it.event_type && (
              <span className="rounded-full bg-[#C05A3A]/15 text-[#A54C30] px-2.5 py-0.5 text-[12px]">
                {it.event_type}
              </span>
            )}
          </div>
          <div className="flex gap-3 mt-3">
            <MessageSquare className="h-4 w-4 mt-1 text-[#C05A3A]" strokeWidth={1.5} />
            <p className="text-[#232321] leading-relaxed whitespace-pre-wrap">{it.details}</p>
          </div>
        </li>
      ))}
    </ul>
  );
}

function Header({ it }) {
  return (
    <div className="flex flex-wrap items-baseline justify-between gap-3 mb-3">
      <div className="font-serif-display text-xl">{it.name}</div>
      <div className="text-[12px] uppercase tracking-[0.22em] text-[#5C5C59]">
        {new Date(it.created_at).toLocaleString()} · {(it.language || "en").toUpperCase()}
      </div>
    </div>
  );
}

function Contacts({ it }) {
  return (
    <div className="grid sm:grid-cols-2 gap-3 text-sm text-[#5C5C59]">
      <div className="flex items-center gap-2">
        <Mail className="h-4 w-4" strokeWidth={1.5} />
        <a href={`mailto:${it.email}`} className="hover:text-[#C05A3A]">{it.email}</a>
      </div>
      {it.phone && (
        <div className="flex items-center gap-2">
          <PhoneIcon className="h-4 w-4" strokeWidth={1.5} />
          <a href={`tel:${it.phone}`} className="hover:text-[#C05A3A]">{it.phone}</a>
        </div>
      )}
    </div>
  );
}

function Empty({ children }) {
  return (
    <div className="rounded-2xl border border-dashed border-[#E2D9CD] p-14 text-center text-[#5C5C59]">
      {children}
    </div>
  );
}
